import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ArrowLeft, Send, Loader2, RotateCcw, Sparkles } from 'lucide-react';
import { geeveeApi } from '../../../api/geevee';
import { setActiveSession } from '../../../store/slices/messengerSlice';
import FollowupChoices from './FollowupChoices';

const WELCOME = "Hi, I'm GeeVee — your AI assistant. Ask me about listings, suburbs, agents, sold comparables or anything in the portfolio and I'll dig through the data for you.";

const formatMessages = (raw = []) =>
  raw.map((m) => ({
    type: m.role === 'bot' ? 'bot' : 'user',
    content: m.content,
  }));

/**
 * GeeVee AI assistant rendered as a pinned conversation inside the CRM
 * messenger thread pane (Meta-AI-in-Messenger pattern). It's a virtual
 * conversation — selected via the sentinel activeSessionId === 'geevee', with
 * no DB session row in the inbox.
 *
 * Talks to the Claude-powered, PERSISTENT agent endpoints
 * (/api/chatbot/agent/geevee/...) — the same LLM the buyer portal uses. The
 * conversation is resumed from the backend on mount, so history survives
 * navigation and reloads instead of resetting to a blank welcome.
 */
const GeeVeeAssistantThread = () => {
  const dispatch = useDispatch();
  const [messages, setMessages] = useState([{ type: 'bot', content: WELCOME }]);
  const [draft, setDraft] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  // Resume (or create) the persistent GeeVee conversation on open and load its
  // full history so the agent picks up where they left off.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const session = await geeveeApi.start(false);
        if (cancelled) return;
        setSessionId(session.id);
        const formatted = formatMessages(session.messages);
        if (formatted.length > 0) setMessages(formatted);
      } catch (e) {
        // Leave the local welcome in place; sends will surface a graceful error.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isTyping, isLoading]);

  const startNewChat = async () => {
    if (isLoading) return;
    setMessages([{ type: 'bot', content: WELCOME }]);
    setDraft('');
    setSessionId(null);
    try {
      const session = await geeveeApi.start(true);
      setSessionId(session.id);
      const formatted = formatMessages(session.messages);
      if (formatted.length > 0) setMessages(formatted);
    } catch (e) {
      // ignore — next send will retry
    }
  };

  const handleSend = () => sendText(draft.trim());

  // Send an explicit message string — used by the composer and by the tappable
  // follow-up option chips (FollowupChoices).
  const sendText = async (content) => {
    if (!content || isLoading) return;

    setMessages((prev) => [...prev, { type: 'user', content }]);
    setDraft('');
    setIsLoading(true);
    setIsTyping(true);

    // Make sure we have a session even if the initial start() failed.
    let activeSessionId = sessionId;
    if (!activeSessionId) {
      try {
        const session = await geeveeApi.start(false);
        activeSessionId = session.id;
        setSessionId(session.id);
      } catch (e) {
        setIsLoading(false);
        setIsTyping(false);
        setMessages((prev) => [...prev, {
          type: 'bot',
          content: "I'm having trouble reaching the assistant right now. Please try again in a moment.",
        }]);
        return;
      }
    }

    let botIndex = null;
    const appendChunk = (chunk) => {
      setIsTyping(false);
      setMessages((prev) => {
        const next = [...prev];
        if (botIndex === null) {
          next.push({ type: 'bot', content: chunk });
          botIndex = next.length - 1;
        } else {
          next[botIndex] = { ...next[botIndex], content: (next[botIndex].content || '') + chunk };
        }
        return next;
      });
    };

    await geeveeApi.sendMessageStream(
      activeSessionId,
      content,
      appendChunk,
      (data) => {
        setIsLoading(false);
        setIsTyping(false);
        const hasFollowup = data && data.followup
          && Array.isArray(data.followup.options)
          && data.followup.options.length > 0;
        if (!hasFollowup) return;
        setMessages((prev) => {
          const next = [...prev];
          if (botIndex !== null && next[botIndex]) {
            next[botIndex] = { ...next[botIndex], followup: data.followup };
          } else {
            next.push({ type: 'bot', content: '', followup: data.followup });
          }
          return next;
        });
      },
      () => {
        setIsLoading(false);
        setIsTyping(false);
        setMessages((prev) => {
          // Only surface the error bubble if nothing streamed in.
          if (botIndex !== null) return prev;
          return [...prev, {
            type: 'bot',
            content: "I'm having trouble reaching the assistant right now. Please try again in a moment.",
          }];
        });
      },
    );
  };

  return (
    <div className="grid h-full min-h-0 bg-white min-w-0" style={{ gridTemplateRows: 'auto minmax(0, 1fr) auto' }}>
      {/* Header */}
      <div className="px-3 py-2.5 md:px-4 md:py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button
            type="button"
            onClick={() => dispatch(setActiveSession(null))}
            className="lg:hidden p-1 -ml-1 text-gray-700 hover:text-blue-700 flex-shrink-0"
            aria-label="Back to conversations"
          >
            <ArrowLeft size={22} />
          </button>
          <div className="relative flex-shrink-0">
            <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-white overflow-hidden flex items-center justify-center border border-blue-100 shadow-sm">
              <img src="/GeeVee-head.svg" alt="GeeVee" className="w-full h-full object-cover" />
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate flex items-center gap-1.5">
              GeeVee
              <Sparkles size={13} className="text-[#2DC8DB] flex-shrink-0" />
            </div>
            <div className="text-[11px] text-gray-500 truncate">AI assistant · Always available</div>
          </div>
        </div>
        <button
          type="button"
          onClick={startNewChat}
          disabled={isLoading}
          title="Start a new conversation"
          className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-[#2DC8DB] disabled:opacity-50 flex-shrink-0"
        >
          <RotateCcw size={13} />
          <span className="hidden sm:inline">New chat</span>
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="min-h-0 overflow-y-auto px-4 py-3 bg-gray-50">
        {messages.map((message, index) => (
          <div key={index} className={`flex mb-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            {message.type === 'user' ? (
              <div className="max-w-[72%] rounded-2xl rounded-br-md px-3.5 py-2.5 bg-blue-600 text-white text-sm leading-relaxed whitespace-pre-wrap break-words shadow-sm">
                {message.content}
              </div>
            ) : (
              <div className="flex items-start gap-2 max-w-[80%]">
                <div className="w-7 h-7 rounded-full bg-white overflow-hidden flex items-center justify-center flex-shrink-0 border border-blue-100">
                  <img src="/GeeVee-head.svg" alt="GeeVee" className="w-full h-full object-cover" />
                </div>
                <div className="min-w-0">
                  <div className="rounded-2xl rounded-bl-md px-3.5 py-2.5 bg-white border border-gray-200 text-gray-900 text-sm leading-relaxed whitespace-pre-wrap break-words shadow-sm">
                    {message.content}
                  </div>
                  {message.followup && index === messages.length - 1 && !isLoading && (
                    <FollowupChoices
                      followup={message.followup}
                      onSubmit={(text) => sendText(text)}
                      disabled={isLoading}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-white overflow-hidden flex items-center justify-center flex-shrink-0 border border-blue-100">
                <img src="/GeeVee-head.svg" alt="GeeVee" className="w-full h-full object-cover" />
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-3.5 py-2.5 shadow-sm">
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-[11px] italic">GeeVee is thinking…</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div
        className="px-3 py-2.5 border-t border-gray-200 flex gap-2 items-end bg-white"
        style={{ paddingBottom: 'calc(0.625rem + env(safe-area-inset-bottom))' }}
      >
        <textarea
          ref={textareaRef}
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Ask GeeVee about listings, suburbs, the portfolio…"
          className="flex-1 resize-none text-sm px-4 py-2 border border-gray-200 rounded-2xl bg-gray-50 text-gray-900 outline-none focus:border-blue-400 focus:bg-white max-h-32"
          style={{ minHeight: '2.25rem' }}
          disabled={isLoading}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!draft.trim() || isLoading}
          className="w-9 h-9 rounded-full bg-gradient-to-r from-[#2DC8DB] to-[#25ABC0] text-white flex items-center justify-center hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          title="Send"
        >
          <Send size={15} />
        </button>
      </div>
    </div>
  );
};

export default GeeVeeAssistantThread;
